import { Close } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { DataType, SortDirection } from "ka-table";
import { Column } from "ka-table/models";
import { useMemo } from "react";
import FilterChips from "../../../App/Components/Filters/FilterChips";
import SearchFilters from "../../../App/Components/Filters/SearchFilters";
import CrmTable from "../../../App/Components/Table/CrmTable";
import { ICandidate } from "../../../App/Typings/Candidate";
import { IGroup } from "../../../App/Typings/Groups";
import { ITablePagination } from "../../../App/Typings/SortableTable";
import { clearOldLocalStorage } from "../../../App/Utilities/browserStorage";
import CandidatesSearchInput from "../../../ManageCandidates/Components/CandidatesSearchInput/CandidatesSearchInput";
import { AddToGroupDialogStateType } from "./State/useAddToGroupDialogState";

const columnNames: Column[] = [
  {
    title: "Date Added",
    key: "createdDate",
    dataType: DataType.Date,
    sortDirection: SortDirection.Descend,
    isSortable: true,
  },
  { title: "Name", key: "fullName", dataType: DataType.String },
  { title: "Company", key: "company", dataType: DataType.String, visible: false },
  { title: "Title", key: "title", dataType: DataType.String, visible: false },
  { title: "Tags", key: "tags", dataType: DataType.Object },
  { title: "Attachments", key: "attachments", dataType: DataType.Object },
  { title: "Last Updated", key: "lastUpdatedDate", dataType: DataType.Date },
];

export default function AddToGroupDialog({
  group,
  state,
}: {
  group: IGroup;
  state: AddToGroupDialogStateType;
}) {
  const {
    getters: {
      candidateList,
      dialogOpen,
      disableSelectAll,
      filterQueryString,
      isCandidateSelected,
      isRowDisabled,
      selectAll,
      isLoadingAddMembers,
      tags,
      timeFilter,
      filteredCandidateListIsLoading,
      search,
      selectedCount,
      sortBy,
      sortDirection,
      page,
      pageSize,
      totalItems,
    },
    handlers: {
      handleClose,
      handleSave,
      handleSaveClick,
      handleChangePage,
      handleRequestSort,
      handleChangePageSize,
      handleClickCandidateRow,
      handleSelectAllClick,
      handleSearchChange,
      handleTagsChange,
      handleTimeFilterChange,
    },
  } = state;

  const allColumns = useMemo(
    () =>
      columnNames.map((col) => {
        if (col.key === sortBy) {
          return {
            ...col,
            sortDirection: sortDirection === "desc" ? SortDirection.Descend : SortDirection.Ascend,
            isSortable: true,
          };
        }
        return {
          ...col,
          sortDirection: undefined,
        };
      }),
    [sortBy, sortDirection],
  );

  return (
    <Dialog
      id="addtogroup-dialog"
      aria-labelledby="addtogroup-title"
      open={dialogOpen}
      onClose={handleClose}
      scroll="paper"
      fullWidth={true}
      maxWidth="lg"
    >
      <DialogTitle
        id="addtogroup-title"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 1,
        }}
      >
        <Box>Add Candidates to {group.name}</Box>
        <Box>
          <IconButton
            onClick={handleClose}
            aria-label="Close Add to Group Window"
            aria-controls="addtogroup-dialog"
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers={true}>
        <Grid container>
          <Grid container item xs={12} sm={12} md={10} spacing={2}>
            <Grid item xs={12} sm={12} md={4}>
              <CandidatesSearchInput search={search} handleSearchChange={handleSearchChange} />
            </Grid>
            <Grid container item xs={12} md={8} spacing={2}>
              <SearchFilters
                isLoading={filteredCandidateListIsLoading}
                filterQueryString={filterQueryString}
                setTags={handleTagsChange}
                setTimeFilter={handleTimeFilterChange}
                tags={tags}
                timeFilter={timeFilter}
                id="addtogroup"
              />
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12} md={2}>
            <Button
              onClick={async () => await handleSaveClick(handleSave)}
              variant="contained"
              disabled={isLoadingAddMembers || selectedCount === 0}
              data-test="addtogroup-addbutton"
              fullWidth
              sx={{ height: "100%" }}
            >
              Add Candidates
            </Button>
          </Grid>
          <Grid item xs={12} style={{ padding: "5px", marginTop: "10px" }}>
            <FilterChips
              handleTagsChange={handleTagsChange}
              handleTimeFilterChange={handleTimeFilterChange}
              tags={tags}
              timeFilter={timeFilter}
              key={"add-to-group-filter-chips"}
            />
          </Grid>
          <Grid item xs={12}>
            <CrmTable
              name="add-to-group-table"
              columns={allColumns}
              data={candidateList}
              loading={filteredCandidateListIsLoading}
              disableSelectAll={disableSelectAll}
              rowSelection={{
                onRowSelect: (row: ICandidate, _selected: boolean) => handleClickCandidateRow(row),
                isRowSelected: isCandidateSelected,
                onAllSelected: handleSelectAllClick,
              }}
              order={sortDirection}
              orderBy={sortBy}
              pagination={
                {
                  totalCount: totalItems,
                  rowsPerPage: [10, 25, 50, 100],
                  enabled: true,
                  currentPage: page,
                  rows: pageSize,
                  handleChangePage: handleChangePage,
                  handleChangeRowsPerPage: handleChangePageSize,
                } as ITablePagination
              }
              onSort={handleRequestSort}
              onRowClick={handleClickCandidateRow}
              isRowDisabled={isRowDisabled}
              isAllSelected={selectAll}
              rowTestId={(row) => `candidate-${row.id}`}
              rowTitle={(row) => (isRowDisabled(row) ? "Candidate is already in the group" : "")}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ py: 2, px: 3 }}>
        <Typography data-test="addcandidates-currently-selected">
          Currently selected: <strong>{selectedCount}</strong> candidates
        </Typography>
      </DialogActions>
    </Dialog>
  );
}

clearOldLocalStorage("add-to-group-table");
